import { Helmet } from 'react-helmet-async'
import { motion } from 'framer-motion'
import { useInView } from 'react-intersection-observer'
import { Award, TrendingUp, Users, Building, Sparkles, Rocket, Globe, Lightbulb, Target, Shield, Zap } from 'lucide-react'

const milestones = [
  {
    year: '2009',
    title: 'Company Founded',
    description: 'Eonixa Dynamics was established in Pune, Maharashtra with a vision to transform government technology and infrastructure.',
    icon: Building,
    color: '#6366f1',
    highlight: 'The Beginning',
  },
  {
    year: '2010',
    title: 'First Government Project',
    description: 'Secured our first major government contract, marking the beginning of our journey in public sector technology solutions.',
    icon: Award,
    color: '#8b5cf6',
    highlight: 'Milestone',
  },
  {
    year: '2012',
    title: 'ISO 9001 Certification',
    description: 'Achieved ISO 9001:2015 certification for Quality Management Systems, demonstrating our commitment to excellence.',
    icon: Shield,
    color: '#ec4899',
    highlight: 'Quality First',
  },
  {
    year: '2014',
    title: 'Education Technology',
    description: 'Entered the Education Technology sector with smart classroom solutions and digital learning platforms.',
    icon: Lightbulb,
    color: '#f59e0b',
    highlight: 'Expansion',
  },
  {
    year: '2016',
    title: 'Healthcare Vertical',
    description: 'Launched healthcare solutions division with hospital management systems and medical technology solutions.',
    icon: Users,
    color: '#10b981',
    highlight: 'New Sector',
  },
  {
    year: '2018',
    title: 'Multiple ISO Certifications',
    description: 'Achieved ISO 14001, ISO 27001, and ISO 45001 certifications, becoming a multi-certified organization.',
    icon: Award,
    color: '#06b6d4',
    highlight: 'Certified',
  },
  {
    year: '2020',
    title: 'Pan-India Presence',
    description: 'Expanded operations across India with offices in major cities, serving clients nationwide.',
    icon: Globe,
    color: '#8b5cf6',
    highlight: 'National',
  },
  {
    year: '2022',
    title: '100+ Projects Milestone',
    description: 'Successfully completed 100+ government projects across all verticals with measurable impact.',
    icon: Target,
    color: '#ec4899',
    highlight: 'Achievement',
  },
  {
    year: '2024',
    title: '₹150 Crore Enterprise',
    description: 'Evolved into a multi-sector enterprise valued at ₹150 Crores with diversified portfolio and strong growth.',
    icon: TrendingUp,
    color: '#f59e0b',
    highlight: 'Growth',
  },
  {
    year: '2025',
    title: 'Digital Innovation Hub',
    description: 'Established R&D center for AI/ML and IoT innovations, driving the next generation of government technology.',
    icon: Rocket,
    color: '#10b981',
    highlight: 'Innovation',
  },
]

const stats = [
  { icon: Building, value: '15+', label: 'Years Experience' },
  { icon: Award, value: '10', label: 'ISO Certifications' },
  { icon: Users, value: '50+', label: 'Team Members' },
  { icon: Target, value: '100+', label: 'Projects Delivered' },
]

export default function Timeline() {
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true,
  })

  return (
    <>
      <Helmet>
        <title>Our Journey | Eonixa Dynamics</title>
        <meta name="description" content="15+ years of excellence - From a startup to a leading Government Technology enterprise. Discover our milestones and growth story." />
      </Helmet>

      <section className="min-h-screen relative pt-20 pb-16">
        {/* Background is global in App.tsx */}
        <div className="container-enterprise relative z-10">
          {/* Page Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5 }}
            className="text-center mb-16"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={inView ? { opacity: 1, scale: 1 } : {}}
              transition={{ duration: 0.3 }}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full mb-6"
              style={{
                background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.15) 0%, rgba(139, 92, 246, 0.15) 100%)',
                border: '1px solid rgba(99, 102, 241, 0.25)',
                boxShadow: '0 0 30px rgba(99, 102, 241, 0.1)',
              }}
            >
              <Sparkles className="w-5 h-5 text-primary" />
              <span className="text-sm text-slate-300">Our Story</span>
            </motion.div>
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4">
              Our <span className="gradient-text">Journey</span>
            </h1>
            <p className="text-lg text-slate-400 max-w-3xl mx-auto">
              15+ years of innovation, growth, and transformation. From a small startup to a leading Government Technology enterprise.
            </p>
          </motion.div>

          {/* Stats */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-16"
          >
            {stats.map((stat, index) => {
              const Icon = stat.icon
              return (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, y: 20 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.3, delay: 0.1 + index * 0.1 }}
                  className="text-center p-6 rounded-2xl"
                  style={{
                    background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.08) 0%, rgba(139, 92, 246, 0.05) 100%)',
                    border: '1px solid rgba(255, 255, 255, 0.06)',
                  }}
                >
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-3"
                    style={{
                      background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.2) 0%, rgba(139, 92, 246, 0.2) 100%)',
                    }}
                  >
                    <Icon className="w-6 h-6 text-primary" />
                  </div>
                  <p className="text-2xl font-bold text-white mb-1">{stat.value}</p>
                  <p className="text-sm text-slate-400">{stat.label}</p>
                </motion.div>
              )
            })}
          </motion.div>

          {/* Timeline */}
          <div ref={ref} className="relative">
            {/* Timeline Line */}
            <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5"
              style={{
                background: 'linear-gradient(180deg, #6366f1 0%, #8b5cf6 25%, #ec4899 50%, #f59e0b 75%, #10b981 100%)',
              }}
            />

            <div className="space-y-12">
              {milestones.map((milestone, index) => {
                const Icon = milestone.icon
                const isEven = index % 2 === 0
                return (
                  <motion.div
                    key={milestone.year}
                    initial={{ opacity: 0, x: isEven ? -50 : 50 }}
                    animate={inView ? { opacity: 1, x: 0 } : {}}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className={`relative flex items-center gap-8 ${isEven ? 'md:flex-row' : 'md:flex-row-reverse'}`}
                  >
                    {/* Content */}
                    <div className={`flex-1 ${isEven ? 'md:text-right' : 'md:text-left'} pl-12 md:pl-0`}>
                      <div className="rounded-2xl p-6 transition-all duration-300 hover:translate-y-[-2px]"
                        style={{
                          background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.08) 0%, rgba(139, 92, 246, 0.05) 100%)',
                          border: '1px solid rgba(255, 255, 255, 0.06)',
                          backdropFilter: 'blur(12px)',
                        }}
                      >
                        <div className={`flex items-center gap-2 mb-3 ${isEven ? 'md:justify-end' : 'md:justify-start'}`}>
                          <span className="px-3 py-1 text-xs font-medium rounded-full"
                            style={{
                              background: `${milestone.color}20`,
                              color: milestone.color,
                              border: `1px solid ${milestone.color}30`,
                            }}
                          >
                            {milestone.highlight}
                          </span>
                          <span className="text-2xl font-bold gradient-text">{milestone.year}</span>
                        </div>
                        <h3 className="text-lg font-semibold text-white mb-2">
                          {milestone.title}
                        </h3>
                        <p className="text-sm text-slate-400">{milestone.description}</p>
                      </div>
                    </div>

                    {/* Icon */}
                    <div className="absolute left-4 md:left-1/2 md:-translate-x-1/2 flex items-center justify-center">
                      <div className="w-12 h-12 rounded-xl flex items-center justify-center"
                        style={{
                          background: milestone.color,
                          boxShadow: `0 0 20px ${milestone.color}40`,
                        }}
                      >
                        <Icon className="w-6 h-6 text-white" />
                      </div>
                    </div>

                    {/* Spacer */}
                    <div className="hidden flex-1 md:block" />
                  </motion.div>
                )
              })}
            </div>
          </div>

          {/* Bottom CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.6 }}
            className="mt-16 text-center"
          >
            <div className="inline-flex items-center gap-4 px-8 py-4 rounded-2xl"
              style={{
                background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.05) 100%)',
                border: '1px solid rgba(16, 185, 129, 0.2)',
              }}
            >
              <div className="w-10 h-10 rounded-xl flex items-center justify-center"
                style={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)' }}
              >
                <Zap className="w-5 h-5 text-white" />
              </div>
              <div className="text-left">
                <p className="text-sm font-semibold text-white">Growing Stronger Every Day</p>
                <p className="text-xs text-slate-400">Building the future of Government Technology</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  )
}